import React, { createContext, useContext, useState } from "react";
import { usePermissionsContext } from "../context/permissionContext";
import de from "../i18n/de";
import en from "../i18n/en";
import it from "../i18n/it";
import { LanguageCode } from "../types";

export type Translation = typeof en;
export type TranslationKeys = keyof Translation;

const translations: Record<string, Translation> = { en, it, de };

export type I18n = {
  t: Translation;
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
  errorsMap: Map<number, string>;
};

const I18nContext = createContext<I18n | null>(null);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const permissions = usePermissionsContext();
  const [language, setLanguage] = useState<LanguageCode>(permissions.preferredLanguage ?? "en-UK");

  const t = translations[language.substring(2, 0)] ?? en;

  const errorsMap = new Map<number, string>([
    [100001, t.processingRequests.errors.notFound],
    [100004, t.processingRequests.errors.missingFinalItem],
    // [100008, t.processingRequests.errors.missingPackagingsOrLabels],
    [100009, t.processingRequests.errors.missingExpectedMinutes],
    [100010, t.processingRequests.errors.missingProductionMinutes],
    [100013, t.processingRequests.errors.invalidAvailabilityDate],

    [500001, t.suppliers.errors.notFound],
    [500004, t.suppliers.errors.codeUsed],
    [500006, t.suppliers.errors.inUseByRequests],

    [600001, t.processingRequestTypes.errors.notFound],
    [600003, t.processingRequestTypes.errors.nameUsed],
    [600004, t.processingRequestTypes.errors.inUseByRequests],

    [700001, t.labels.errors.notFound],
    [700002, t.labels.errors.codeExists],
    [700005, t.labels.errors.inUseByRequests],

    [800001, t.packagings.errors.notFound],
    [800004, t.packagings.errors.codeExists],
    [800005, t.packagings.errors.inUseByRequests],

    [1000001, t.priorities.errors.notFound],
    [1000004, t.priorities.errors.nameUsed],
    [1000005, t.priorities.errors.inUseByRequests],

    [1100001, t.costCenters.errors.notFound],
    [1100004, t.costCenters.errors.codeUsed],
    [1100005, t.costCenters.errors.nameUsed],
    [1100006, t.costCenters.errors.inUseByRequests],

    [1200001, t.users.errors.notFound],
    [1200003, t.users.errors.alreadyExists],
    [1200008, t.users.errors.emailAlreadyExists],

    [1300001, t.items.errors.notFound],
    [1300002, t.items.errors.alreadyExists],
    [1300008, t.items.errors.inUseByRequests],

    [1900002, t.documents.errors.nameAlreadyExists],
    [1900008, t.documents.errors.extensionChanged],
    [1900009, t.documents.errors.sizeTooLarge],
  ]);

  return <I18nContext.Provider value={{ t, language, setLanguage, errorsMap }}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used inside an I18nProvider");
  }
  return context;
};
